<template>
  <div class="container-fluid page-container">
    <ban-edit-profile v-if="isBannedFromEditing" />
    <div v-else-if="!busy">
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col align-items-center'" :title="$t('User Details')" />
      </div>
      <div class="row">
        <div class="col-12">
          <div class="bg-white col-12 rounded shadow border p-4">
            <b-alert :variant="'danger'" :show="alertMessage !== ''">
              {{ alertMessage }}
            </b-alert>
            <form @submit.prevent="onSaveDetails">
              <validation-observer ref="detailsObserver">
                <div class="row">
                  <div class="col col-lg-4">
                    <b-alert variant="warning" :show="true">
                      {{ $t('To update this information, please contact your account manager or our team') }}
                      <router-link :to="{name: 'contact'}">
                        {{ $t('here') }}
                      </router-link>
                    </b-alert>
                    <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required">
                      <b-form-group :label="`${ $t('Account Email') } *`">
                        <b-form-input v-model="email" type="email" :state="(dirty || validated) ? (valid ? null : false) : null" :disabled="true" />
                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                    <b-form-group>
                      <template slot="label">
                        <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0">
                          {{ $t('Email Leads to') }}
                          <router-link class="label-link" :to="{name: 'lead-alerts'}">
                            {{ $t('Edit in Lead Alerts') }}
                          </router-link>
                        </legend>
                      </template>
                      <b-form-input v-model="leadEmails" disabled :placeholder="$t('Account Email')" />
                    </b-form-group>
                    <b-form-group v-if="$store.state.company.COMPANY.paid === false">
                      <template slot="label">
                        <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0">
                          {{ $t('Email Invoices to (comma separated list)') }}
                        </legend>
                      </template>
                      <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="is-email-string">
                        <b-form-input v-model="invoiceEmail" :state="(dirty || validated) ? (valid ? null : false) : null" :disabled="true" />
                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </validation-provider>
                    </b-form-group>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col col-lg-4">
                    <b-form-group>
                      <label class="py-2">{{ $t('Communication Preferences') }}</label>
                      <div class="align-items-center d-flex w-100">
                        <b-form-checkbox v-model="marketingPreference" class="float-left" />
                        <span class="ml-2 float-left small">{{ $t('I would like to receive marketing communications from Leads.io') }}</span>
                      </div>
                    </b-form-group>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col col-lg-4">
                    <b-form-group>
                      <div class="align-items-center d-flex w-100">
                        <b-form-checkbox v-model="invoiceEmailOnly" class="float-left" />
                        <span class="ml-2 float-left small">{{ $t('Only send invoices/reminders to the invoice email addresses') }}</span>
                      </div>
                    </b-form-group>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <b-button variant="outline-secondary" size="sm" class="float-left" @click="onResetValues">
                      {{ $t('Cancel') }}
                    </b-button>
                    <div class="d-inline-block text-center ml-2 submit-button">
                      <b-button
                        variant="primary"
                        size="sm"
                        :disabled="isSavingUserDetails"
                        :class="['animated-submit', submitClassObject, {'disabled': isSavingUserDetails, 'animate': animateSubmitButton}]"
                        type="submit"
                      >
                        {{ $t('Save') }}
                      </b-button>
                    </div>
                  </div>
                </div>
              </validation-observer>
              <div class="row">
                <div class="col col-lg-4 mt-3 mb-2">
                  <hr class="w-100">
                </div>
              </div>
            </form>
            <div class="row">
              <div class="col col-lg-4">
                <form @submit.prevent="onUpdatePassword">
                  <validation-observer ref="passwordObserver">
                    <validation-provider v-slot="{valid, invalid, errors}" rules="required|valid-password|confirmed:confirmation">
                      <b-form-group :label="$t('Type your new password')">
                        <b-input-group>
                          <template v-slot:append>
                            <b-input-group-text
                              v-if="newPassword !== ''"
                              class="border-left-0 bg-white"
                            >
                              <strong v-if="valid" class="text-success">
                                <font-awesome-icon icon="check-circle" />
                              </strong>
                              <strong v-else class="text-danger"
                                      :title="$t('Passwords must have 7+ characters, at least 1 number, at least 1 letter, and no whitespace')"
                              >
                                <font-awesome-icon icon="times-circle" />
                              </strong>
                            </b-input-group-text>
                          </template>
                          <b-form-input v-model="newPassword" :class="[newPassword !== '' ? 'border-right-0' : '']"
                                        type="password"
                          />
                        </b-input-group>
                        <password v-model="newPassword" :strength-meter-only="true" />
                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                    <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required" vid="confirmation">
                      <b-form-group :label="$t('Confirm your new password')">
                        <b-form-input
                          v-model="confirmedPassword"
                          type="password"
                          :state="(dirty || validated) ? (valid ? null : false) : null"
                        />
                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </validation-observer>
                  <div class="row">
                    <div class="col-12">
                      <b-button variant="outline-secondary" size="sm" @click="onResetPassword">
                        {{ $t('Cancel') }}
                      </b-button>
                      <div class="d-inline-block text-center ml-2 submit-button">
                        <b-button
                          variant="primary"
                          size="sm"
                          :class="['animated-submit', updatePasswordClassObject, {'animate': isSavingUserPassword}]"
                          type="submit"
                        >
                          {{ $t('Update Password') }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <spinner v-else />
  </div>
</template>

<script>
import PageHeading from "@/components/control/PageHeading";
import BanEditProfile from "@/components/control/BanEditProfile";
import Spinner from "@/components/animation/Spinner";
import notification from "@/imports/mixins/notification";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import isBannedFromEditing from "@/imports/mixins/isBannedFromEditing";
import submitAnimationHandler from "@/imports/mixins/submitAnimationHandler";

export default {
  name: "UserDetails",
  components: {
    PageHeading,
    BanEditProfile,
    Spinner,
    ValidationProvider,
    ValidationObserver,
    Password: () => import('vue-password-strength-meter')
  },
  mixins: [notification, isBannedFromEditing, submitAnimationHandler],
  data() {
    return {
      busy: true,
      email: '',
      leadEmails: '',
      marketingPreference: false,
      newPassword: '',
      confirmedPassword: '',
      alertMessage: '',
      alertVariant: '',
      isSavingUserDetails: false,
      isSavingUserPassword: false,
      animatedPasswordError: false,
      animatedPasswordSuccess: false,
      invoiceEmail: '',
      invoiceEmailOnly: false,
    }
  },
  computed: {
    isValidPassword() {
      return (this.newPassword.search(/[a-z]/i) > -1)
        && (this.newPassword.search(/[0-9]/) > -1)
        && (this.newPassword.length > 6);
    },
    updatePasswordClassObject: function () {
      return {
        'error': this.animatedPasswordError,
        'success': this.animatedPasswordSuccess,
      }
    }
  },
  methods: {
    onResetValues() {
      this.busy = false;
      this.email = this.$store.state.company.COMPANY_USER.email;
      this.leadEmails = this.$store.state.company.COMPANY_USER.company.quote_emails.join(', ');
      this.marketingPreference = this.$store.state.company.COMPANY_USER.company.receive_marketing_email;
      this.invoiceEmail = this.$store.state.company.COMPANY.invoice_emails.map(email => email.email).join(', ');
      this.invoiceEmailOnly = this.$store.state.company.COMPANY_USER.invoice_email_only;
    },
    onResetPassword() {
      this.newPassword = '';
      this.confirmedPassword = '';
    },
    onSaveDetails() {
      this.$refs.detailsObserver.validate().then(success => {
        if (!success) {
          return;
        }

        this.alertMessage = '';
        this.alertVariant = '';
        this.isSavingUserDetails = true;
        this.animateSubmitButton = true;
        this.$store.dispatch('updateCompanyUser', {
          email: this.email,
          marketingPreference: this.marketingPreference,
          invoiceEmail: this.invoiceEmail,
          invoiceEmailOnly: this.invoiceEmailOnly,
        }).then(({success, errors}) => {
          if (success) {
            this.notify(this.$bvToast, this.$t('Your details have been successfully saved'), null, 'success');
            this.enableSubmitAnimation(true);
          } else {
            this.alertVariant = 'danger';
            this.alertMessage = errors.join('\n');
            this.enableSubmitAnimation(false);
          }
        }).finally(() => this.isSavingUserDetails = false);
      });
    },
    onUpdatePassword() {
      this.$refs.passwordObserver.validate().then(success => {
        if (!success) {
          return;
        }

        this.alertVariant = '';
        this.alertMessage = '';
        this.isSavingUserPassword = true;
        this.$api.company.updatePassword(this.newPassword, this.confirmedPassword).then((result) => {
          if (result.data.errors) {
            this.toggleUpdatePasswordAnimation(false);
            this.alertVariant = 'danger';
            this.alertMessage = result.data.errors.join(', ');
          } else {
            this.toggleUpdatePasswordAnimation(true);
            this.notify(this.$bvToast, this.$t('Your password has been updated'), null, 'success');
          }
        }).catch((err) => {
          this.alertVariant = 'danger';
          this.alertMessage = err.response.data.errors.join(', ');
        }).finally(() => this.isSavingUserPassword = false);
      });
    },
    toggleUpdatePasswordAnimation: function (status) {
      if (status) {
        this.animatedPasswordSuccess = true;
        setTimeout(() => {
          this.animatedPasswordSuccess = false;
        }, 2000);
      } else {
        this.animatedPasswordError = true;
        setTimeout(() => {
          this.animatedPasswordError = false;
        }, 2000);
      }
    }
  },
  created() {
    this.$store.dispatch("getCompanyUser").then(() => this.onResetValues());
    this.$store.dispatch("getCompany").then(() => this.onResetValues());
  },
}
</script>

<style scoped lang="scss">
.label-link {
  float: right;
  font-size: .8em;
  text-decoration: underline;
}

.submit-button,
.animated-submit {
  min-width: 120px;
}
</style>
